/* eslint-disable no-confusing-arrow */
import React, { useState, useCallback, useRef, lazy, Suspense } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Material
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';

// ReactCSV
import { CSVLink } from 'react-csv';

// Helpers
import { formatDateAndTime } from 'helpers/date';

// Actions
import { setOpen, setClose } from 'store/ducks/dialogs/actions';
import * as EmployeesActions from 'store/ducks/employees/actions';

// Types
import { ApplicationState } from 'store';

// Contants
import BALANCE from 'constants/balance';
import ITEMS from 'constants/itemsByFilter';

// Constants
import { REPORTS_TYPES } from 'constants/report';
import TITLES_OPTIONS from 'constants/infoModulesTitle';

// Components
import {
  MainContent,
  DialogueExport,
  SearchComponent,
  XlsHeaderInterface,
  XlsRowInterface,
  ReportTitle,
} from 'components';
import { TablePlaceholder } from 'components/TablePlaceholder';
import { TabPanel } from 'components/TabPanel';
import TableOperatorCommission from './components/TableOperatorCommission/TableOperatorCommission';
import { Submissions, History } from '../../../../components';

// Styles
import useStyles from './OperatorCommissionReportDatails.styles';

// Lazy Components
const Filter = lazy(() => import('components/Filter'));

interface OperatorCommissionReportDatailsProps {
  setSubmissionData: (param: any) => void;
  setIsAddSubmissiion: (param: any) => void;
  setIsEditSubmissiion: (param: any) => void;
}

const OperatorCommissionReportDatails: React.FC<OperatorCommissionReportDatailsProps> = ({
  setSubmissionData,
  setIsAddSubmissiion,
  setIsEditSubmissiion,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const csvRef = useRef(null);

  const dialog = useSelector((state: ApplicationState) => state.dialogs.dialog);

  const [xlsHeader, setXlsHeader] = useState<XlsHeaderInterface[]>();
  const [xlsRows, setXlsRows] = useState<Array<XlsRowInterface[]>>([]);
  const [generalNavGuide, setGeneralNavGuide] = useState(0);
  const [tableReference, setTableReference] = useState<unknown>();
  const [reportIsVisible, setReportIsVisible] = useState(false);
  const [tableCSV, setTableCSV] = useState('');
  const [periodString, setPeriodString] = useState('');
  const [simplePdfData, setSimplePdfData] = useState<{
    title: string;
    subtitle: string;
    filters: string;
    headers: any;
    rows: any;
  }>({
    title: '',
    subtitle: '',
    filters: '',
    headers: [],
    rows: [],
  });

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setGeneralNavGuide(newValue);
  };

  const handleLoadData = useCallback(() => {
    setReportIsVisible(true);

    dispatch(
      EmployeesActions.getEmployeesReportRequest({
        startDate: new Date().setHours(0, 0, 0),
        endDate: new Date().setHours(23, 59, 59),
        _includes: 'token',
      }),
    );
  }, []);

  const handleApply = useCallback(
    ({
      startedAt,
      startDate,
      endDate,
      selectedEmployees,
      selectedProfiles,
      selectedPayments,
      selectedCollection,
    }) => {
      dispatch(setClose({}));
      setReportIsVisible(true);

      const isPeriod = startDate?.getTime() < endDate?.getTime();
      let startDateValue;
      let endDateValue;
      if (isPeriod) {
        startDateValue = new Date(startDate).getTime();
        endDateValue = new Date(endDate).getTime();
      } else if (startedAt) {
        startDateValue = new Date(startedAt).setHours(0, 0, 0);
        endDateValue = new Date(startedAt).setHours(23, 59, 59);
      }
      dispatch(
        EmployeesActions.getEmployeesReportRequest({
          startDate: startDateValue,
          endDate: endDateValue,
          employeeId:
            selectedEmployees?.length > 0
              ? selectedEmployees.map((e: any) => e.id).join()
              : undefined,
          profileId:
            selectedProfiles?.length > 0
              ? selectedProfiles.map((e: any) => e.id).join()
              : undefined,
          usageType:
            selectedPayments?.length > 0
              ? selectedPayments
                  .map(
                    (e: any) =>
                      BALANCE.USAGE_TYPES.find(({ ID }) => ID === e.key)?.KEY,
                  )
                  .join()
              : undefined,
          chargeType:
            selectedCollection !== null ? selectedCollection : undefined,
          _includes: 'token',
        }),
      );

      const tempPeriodString = `De: ${
        isPeriod
          ? formatDateAndTime(new Date(startDate))
          : formatDateAndTime(new Date(startedAt))
      } até: ${
        isPeriod
          ? formatDateAndTime(new Date(endDate))
          : formatDateAndTime(new Date(startedAt))
      }`;

      let updatedPeriodString = tempPeriodString;

      if (selectedPayments?.length > 0) {
        const tempLinesString = `Status do Pagamento: ${selectedPayments
          .map(
            (e: any) => BALANCE.USAGE_TYPES.find(({ ID }) => ID === e.key)?.KEY,
          )
          .join()}`;
        updatedPeriodString += ', ' + tempLinesString;
      }
      if (selectedProfiles?.length > 0) {
        const tempLinesString = `Perfil: ${selectedProfiles
          .map((e: any) => e.id)
          .join()}`;
        updatedPeriodString += ', ' + tempLinesString;
      }
      if (selectedEmployees?.length > 0) {
        const tempLinesString = `Motorista: ${selectedEmployees
          .map((e: any) => e.id)
          .join()}`;
        updatedPeriodString += ', ' + tempLinesString;
      }
      if (selectedCollection?.length > 0) {
        const tempLinesString = `Arrecadação: ${selectedCollection
          .map((e: any) => e.id)
          .join()}`;
        updatedPeriodString += ', ' + tempLinesString;
      }
      setPeriodString(updatedPeriodString);

      // setFilterList(filters);
      if (!reportIsVisible) setReportIsVisible(true);
    },
    [reportIsVisible],
  );

  return (
    <div className={classes.root}>
      <MainContent className={classes.paperRoot}>
        <Grid
          container
          justifyContent='space-between'
          className={classes.gridContainer}>
          <Grid item>
            <ReportTitle
              titleVariant='h4'
              title='Relatórios de Operação • Comissão de Operadores'
              tooltipTitle={
                TITLES_OPTIONS.REPORTS.OPERATION.OPERATORS_COMMITTEE.VALUE
              }
            />
            <Typography variant='h4' color='secondary' />
          </Grid>
        </Grid>
        {reportIsVisible && (
          <Grid item className={classes.icons}>
            <Grid item>
              <CSVLink
                filename='Relatório_de_comissão_de_operadores.csv'
                data={tableCSV}
                ref={csvRef}
              />
            </Grid>
          </Grid>
        )}

        <Grid container className={classes.chipFilter}>
          <Box display='flex' alignItems='center' width='100%' marginTop='8px'>
            <SearchComponent
              disabledInput={!reportIsVisible}
              modalFilter={() => {
                dispatch(
                  setOpen({
                    open: true,
                    content: (
                      <Suspense fallback={<p>Carregando...</p>}>
                        <Filter
                          fields={ITEMS.operatorCommission.remote}
                          handleApply={handleApply}
                        />
                      </Suspense>
                    ),
                    maxWidth: 'xl',
                  }),
                );
              }}
            />
            {/* <Grid container justifyContent='flex-end'>
              <Button
                className={classes.addButton}
                onClick={() => setIsAddSubmissiion(true)}>
                <Typography style={{ marginRight: 8, fontSize: 20 }}>
                  +
                </Typography>{' '}
                <Typography style={{ fontSize: 12 }}>Programar Novo</Typography>
              </Button>
            </Grid> */}
          </Box>
        </Grid>
        <Tabs
          indicatorColor='primary'
          onChange={handleChange}
          style={{
            background: '#ffffff',
            margin: '10px 0px 32px 16px',
          }}
          textColor='primary'
          value={generalNavGuide}>
          <Tab label='Relatório' />
          <Tab label='Envios' />
          <Tab label='Histórico' />
        </Tabs>

        <TabPanel index={0} value={generalNavGuide}>
          {!reportIsVisible && <TablePlaceholder onClick={handleLoadData} />}
          {reportIsVisible && (
            <>
              <Grid container justifyContent='flex-end' alignItems='center'>
                <Box paddingRight='8px' paddingBottom='16px'>
                  <DialogueExport
                    isDialogPrint
                    tableInstance={tableReference}
                    csvRef={csvRef}
                    simplePdf
                    columnsPerPage={5}
                    simplePdfData={simplePdfData}
                    pdfName='tabela comissão de operadores'
                    xlsTitle='Relatório de comissão de operadores'
                    xlsHeader={xlsHeader}
                    xlsRows={xlsRows}
                    pdf
                    csv
                    xls
                  />
                </Box>
              </Grid>
              <Grid ref={(node: unknown) => setTableReference(node)}>
                {dialog.open && dialog.isDialogPrint && (
                  <Typography color='inherit' variant='h4'>
                    Relatórios de Operação • Comissão de Operadores
                  </Typography>
                )}
                <TableOperatorCommission
                  setTableCSV={setTableCSV}
                  periodString={periodString}
                  setSimplePdfData={setSimplePdfData}
                  setXlsHeader={setXlsHeader}
                  setXlsRows={setXlsRows}
                />
              </Grid>
            </>
          )}
        </TabPanel>
        <TabPanel index={1} value={generalNavGuide}>
          <Submissions
            setSubmissionData={setSubmissionData}
            setIsEditSubmissiion={setIsEditSubmissiion}
            reportType={REPORTS_TYPES.EMPLOYEE_ENTRY}
          />
        </TabPanel>
        <TabPanel index={2} value={generalNavGuide}>
          <History reportType={REPORTS_TYPES.EMPLOYEE_ENTRY} />
        </TabPanel>
      </MainContent>
    </div>
  );
};

export default OperatorCommissionReportDatails;
export { OperatorCommissionReportDatails };
